
import { Box, Fade, Typography, useMediaQuery } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import getTheme from '../../theme/theme';
import { PinnacleThemes } from '../../theme/types';
import ArrowRightAltOutlinedIcon from '@mui/icons-material/ArrowRightAltOutlined';
import Sidebar from "../../layouts/Sidebar/Sidebar";
import { PinnacleChip } from "../../components/PinnacleChip";
import { TermsContent } from "../../layouts/TermsAndPrivacy/TermsContent";
import { PrivacyContent } from "../../layouts/TermsAndPrivacy/PrivacyContent";
import { profile } from "../../api/services/user";
import { handleApiError } from "../../utils/errorHandler";
import { pushNotification } from "../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider";
import { NotificationTypes } from "../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider.types";
import { SELECT_VIDEO_PLAYER_FULL_BG } from "../../utils/constants";

export default function AccountTermsAndConditionsPage() {
    const theme = getTheme(PinnacleThemes.BASE_THEME);
    const isMobile = useMediaQuery('(max-width:600px)');
    const [currentContent, setCurrentContent] = useState<string>('terms');
    const [currentContentName, setCurrentContentName] = useState<string>('Terms and Services');
    const [chipButtonName, setChipButtonName] = useState<string>('Privacy Policy');
    const [fadeIn, setFadeIn] = useState<boolean>(true);
    const [unreadNotification, setUnreadNotification] = useState<number>(0);

    const fetchProfile = useCallback(async () => {
        try {
            const response = await profile();
            setUnreadNotification(response.data.notifications);
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
        }
    }, []);

    const handleContent = () => {
        setFadeIn(false);
        setTimeout(() => {
            if (currentContent === 'terms') {
                setCurrentContent('privacy');
                setCurrentContentName('Privacy Policy');
                setChipButtonName('Go to Terms and Services');
            } else {
                setCurrentContent('terms');
                setCurrentContentName('Terms and Services');
                setChipButtonName('Privacy Policy');
            }
            setFadeIn(true);
        }, 300);
    };

    useEffect(() => {
        fetchProfile();
    }, [fetchProfile]);

    return (
        <Box
            sx={{
                width: '100%',
                position: 'relative',
                backgroundColor: 'rgba(0, 8, 15, 1)',
                '&::before': {
                    content: '""',
                    position: 'fixed',
                    top: 0,
                    right: 0,
                    bottom: 0,
                    left: 0,
                    backgroundImage: `url(${SELECT_VIDEO_PLAYER_FULL_BG})`,
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: 'contain',
                    backgroundPosition: 'right top',
                    backgroundAttachment: 'fixed',
                    zIndex: 0,
                    pointerEvents: 'none',
                },
            }}
        >
            {/* Your scrollable content */}
            <main
                style={{
                    display: 'flex',
                    position: 'relative',
                    zIndex: 1,
                }}
            >
                {/* Sidebar */}
                <Sidebar unreadNotification={!!unreadNotification && unreadNotification > 0} />

                {/* Main Content */}
                <Box
                    component="section"
                    sx={{
                        flexGrow: 1,
                        width: '100%',
                        ml: !isMobile ? '72px' : 0,
                        overflowX: 'hidden',
                        mt: !isMobile ? 0 : 7,
                        display: 'flex',
                        justifyContent: 'flex-start',
                    }}
                >
                    <Box
                        sx={{
                            width: '100%',
                            maxWidth: 800,
                            pl: { xs: 2, sm: 10 },
                            pr: { xs: 2, sm: 0 },
                            mt: { xs: 4, sm: 6 },
                            mb: 4,
                        }}
                    >
                        {/* Header */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                mb: 4,
                            }}
                        >
                            <Typography variant="h4" sx={{ fontWeight: 600 }}>
                                {currentContentName}
                            </Typography>

                            <PinnacleChip
                                label={
                                    <span style={{ display: 'flex', alignItems: 'center' }}>
                                        {chipButtonName}
                                        <ArrowRightAltOutlinedIcon sx={{ fontSize: 14, ml: 0.5 }} />
                                    </span>
                                }
                                variant="outlined"
                                color="info"
                                size="small"
                                onClick={handleContent}
                            />
                        </Box>

                        {/* Terms / Privacy Content */}
                        <Fade in={fadeIn} timeout={300}>
                            <Typography
                                component="div"
                                sx={{
                                    fontSize: '14px !important',
                                    color: theme.palette.common.white,
                                }}
                            >
                                {currentContent === 'terms' ? <TermsContent /> : <PrivacyContent />}
                            </Typography>
                        </Fade>
                    </Box>
                </Box>
            </main>
        </Box>
    );
}